import { defineComponent as _defineComponent } from 'vue'
import * as Plot from '@observablehq/plot'
import { ticks } from 'd3'
import { computed, ref, toRef } from 'vue'
import PlotComponent from '@/components/common/PlotComponent.vue'
import { PlotOptions } from '@observablehq/plot'
import _ from 'lodash'
import { SensorSummary } from '@/models/sensors-dto'
import { useTankLevelSensorChartData } from '@/api/sensors'
import moment from 'moment'


export default /*#__PURE__*/_defineComponent({
  __name: 'SensorChartDetails',
  props: {
    sensor: null,
    dateRange: null
  },
  setup(__props: any) {

const props = __props as {
  sensor: SensorSummary
  dateRange: { start: Date; end: Date }
}



const sensorId = computed(() => props.sensor.id)

const propsDateRangeRef = toRef(props, 'dateRange')
const windowStart = computed(() => propsDateRangeRef.value.start)
const windowEnd = computed(() => propsDateRangeRef.value.end)

const { data: tankLevelSensorChartData, isLoading } =
  useTankLevelSensorChartData(sensorId, windowStart, windowEnd)

const panels = ref(0)
const fields = computed(() => {
  const lastPoint = data.value[data.value.length - 1]

  return [
    {
      enabled: lastPoint,
      label: 'Last Reading At',
      value: moment(lastPoint.date).format('MMM DD, YYYY @ hh:mm A'),
    },
    {
      enabled: lastPoint,
      label: 'Last Reading Level',
      value: lastPoint.value.toFixed(2),
      units: 'ft',
    },
    {
      enabled: tankLevelSensorChartData.value,
      label: 'Max Height',
      value: tankLevelSensorChartData.value?.maxValue.toFixed(2),
      units: 'ft',
    },
    {
      enabled: tankLevelSensorChartData.value,
      label: 'Estimated Volume',
      value: (
        lastPoint.value * (tankLevelSensorChartData.value?.factor ?? 0)
      ).toFixed(2),
      units: 'bbls',
    },
  ]
})

const data = computed(() => {
  return (
    tankLevelSensorChartData.value?.readings.map((d) => ({
      ...d,
      date: new Date(d.date),
    })) ?? []
  )
})

const triggers = computed(() => {
  return tankLevelSensorChartData.value?.triggers ?? []
})

const predictedData = computed(() => {
  const lastPoint = data.value[data.value.length - 1]

  if (!triggers.value.length) {
    return []
  }

  const firstTrigger = triggers.value[0]

  const futureDate = new Date(firstTrigger.projectedDate)
  const futureValue =
    firstTrigger.thresholdValue / tankLevelSensorChartData.value!.factor

  if (!lastPoint || !futureDate || futureDate < lastPoint.date) {
    return []
  }

  return [lastPoint, { value: futureValue, date: futureDate }]
})

const events = computed(() => {
  const lastPointOrToday = data.value[data.value.length - 1]?.date ?? new Date()

  return [
    ...(tankLevelSensorChartData.value?.events ?? []),
    {
      date: lastPointOrToday,
      label: moment(lastPointOrToday).isSame(new Date(), 'day')
        ? 'Today'
        : 'Last reading',
    },
  ]
})

const adjustedWindowEnd = computed(() => {
  // window end may show too far into the future and not show any relevant
  // data. In this case we should adjust the window to show only 1-2 days past
  // the last known data point.
  if (data.value.length === 0) {
    return windowEnd.value
  }

  const allPoints = [...data.value, ...predictedData.value]

  const lastDate = _.maxBy(allPoints, (x) => x.date)?.date

  return moment(lastDate).add(2, 'days').toDate()
})

const adjustedWindowStart = computed(() => {
  return windowStart.value
})

const formatDate = (d: Date | string) => {
  return new Date(d).toLocaleDateString(undefined, {
    dateStyle: 'medium',
  })
}

const plotOptions = computed<PlotOptions>(() => {
  const windowStart = adjustedWindowStart.value
  const windowEnd = adjustedWindowEnd.value
  const maxHeight = tankLevelSensorChartData.value?.maxValue ?? 10
  const scale = 10

  return {
    grid: false,
    width: 500,
    height: 220,
    clamp: true,
    label: null,
    marginRight: 0,
    marginLeft: 30,
    x: { domain: [windowStart, windowEnd] },
    y: { domain: [0, maxHeight] },
    marks: [
      Plot.axisY({
        anchor: 'left',
        label: 'height (ft)',
        labelAnchor: 'center',
        ticks: [],
      }),
      Plot.frame({
        fill: '#eaeaea',
        marginBottom: 40,
        marginRight: 0,
      }),
      Plot.axisX({
        ticks: [windowStart],
        tickFormat: (t) => formatDate(t),
        textAnchor: 'start',
        anchor: 'top',
        tickSize: 0,
      }),
      Plot.axisX({
        ticks: [windowEnd],
        tickFormat: (t) => formatDate(t),
        textAnchor: 'end',
        anchor: 'top',
        tickSize: 0,
      }),
      Plot.axisY({
        ticks: ticks(0, maxHeight, scale),
      }),

      // volume area chart
      // predicted
      Plot.areaY(predictedData.value, {
        x: 'date',
        y: 'value',
        fill: '#ccb0f0',
      }),
      // actuals
      Plot.areaY(data.value, { x: 'date', y: 'value', fill: '#b486ef' }),

      // threshold lines
      ...(tankLevelSensorChartData.value?.triggers.map((t) =>
        Plot.ruleY([null], {
          x1: windowStart,
          x2: windowEnd,
          y: t.thresholdValue / tankLevelSensorChartData.value!.factor,
          stroke: '#ccc',
          strokeWidth: 1,
        })
      ) ?? []),

      // show event lines
      Plot.ruleX(events.value, {
        x: (d) => d.date,
        y: maxHeight,
        stroke: (d) => (d.label === 'Today' ? 'red' : '#5468ff'),
        strokeWidth: 2,
      }),

      ...events.value.map((e) =>
        // show text for event lines
        Plot.text([e], {
          text: (d) => d.label + '\n' + formatDate(d.date),
          x: 'date',
          y: 0,
          dy: 16,
          textAnchor: moment(e.date).isSameOrAfter(windowEnd, 'day')
            ? 'end'
            : 'middle',
        })
      ),
    ],
  }
})

return { __sfc: true,props, sensorId, propsDateRangeRef, windowStart, windowEnd, tankLevelSensorChartData, isLoading, panels, fields, data, triggers, predictedData, events, adjustedWindowEnd, adjustedWindowStart, formatDate, plotOptions, PlotComponent }
}

})